import { aaiApi } from '../../services/aaiApi';
import { ingestFile, transcribeAudio } from '../../orbit/lib/intakeApi';
import type { OrbitAsk, OrbitDeckCard, OrbitDeckResponse, OrbitOverview, OrbitRoomPerson } from '../../types/aai';
import type { DeckAnswer, DeckCardModel, DeckLearned, DeckOption, DeckRailState } from '../../components/orbit-deck/types';

/*
 * The server deals cards as data; this file turns them into the card contract
 * and performs each answer through the Room's own endpoints. Every surface
 * that shows a deck for a signed-in person shares it.
 */

const inputFor = (kind: string | undefined): DeckCardModel['input'] => {
  if (kind === 'choose') return 'choose';
  if (kind === 'choose_many') return 'choose_many';
  if (kind === 'people') return 'people';
  if (kind === 'number') return 'number';
  return 'say';
};

export const toDeckCards = (deck: OrbitDeckResponse, people: OrbitRoomPerson[]): DeckCardModel[] => deck.cards.map((c: OrbitDeckCard): DeckCardModel => {
  if (c.kind === 'confirm') {
    return {
      id: c.id, tag: 'confirm', tagLabel: c.tag_label, title: c.title, why: c.why, input: 'confirm',
      facts: (c.facts || []).map((f) => ({ field_key: f.field_key, label: f.label, value: f.value, value_display: f.value_display, source: f.source, confidence: f.confidence, scope: c.scope || '' })),
      source: deck.site.replace(/^https?:\/\//, '') || 'what Orbit read so far',
      confidence: c.facts && c.facts.length ? c.facts.reduce((s, f) => s + f.confidence, 0) / c.facts.length : 0.85,
    };
  }
  if (c.kind === 'confirm_org') {
    return { id: c.id, tag: 'confirm', tagLabel: c.tag_label, title: c.title, why: c.why, input: 'confirm_org', proposals: c.proposals || [] };
  }
  if (c.kind === 'evidence') {
    return { id: c.id, tag: 'evidence', tagLabel: c.tag_label, title: c.title, why: c.why, input: 'evidence' };
  }
  const ask = c.ask as OrbitAsk;
  let search: DeckCardModel['search'];
  if (ask && ask.field_key === 'b.tools_used') search = 'tools';
  return {
    id: c.id, tag: 'ask', tagLabel: c.tag_label, title: c.title, why: c.why, input: inputFor(c.input),
    options: c.options || [], prefill: c.prefill, people: people.map((p) => ({ email: p.email, name: p.name, role: p.role, department_name: p.department_name })),
    fieldKey: ask ? ask.field_key : undefined, meta: { askId: ask ? ask.id : '', personKey: c.person_key || '', intake: c.intake || 'other' }, search,
  };
});

export const searchToolsFor = () => async (q: string): Promise<DeckOption[]> => {
  const res = await aaiApi.searchTools(q);
  return res.data.tools.map((t) => ({ value: t.slug, label: t.label, logo: t.logo, source: t.source }));
};

export const askIdOf = (card: DeckCardModel): string => String((card.meta || {}).askId || '');
const personKeyOf = (card: DeckCardModel): string => String((card.meta || {}).personKey || '');

/** Performs one answer against the Room. Returns nothing; the caller refetches. */
export const performAnswer = async (card: DeckCardModel, answer: DeckAnswer): Promise<void> => {
  if (card.input === 'confirm') {
    if (answer.how === 'edited') return;
    const items = (answer.confirm || []).map((d) => ({ field_key: d.field_key, scope: d.scope || '', correct: d.action === 'correct', skip: d.action === 'skip', value: d.value }));
    await aaiApi.confirmOrbitFacts(items);
    return;
  }
  if (card.input === 'confirm_org') {
    if (answer.how === 'edited' || !answer.org) return;
    await aaiApi.decideOrbitProposals(answer.org);
    return;
  }
  const personKey = personKeyOf(card);
  if (personKey) {
    await aaiApi.answerMyPart({ key: personKey, value: answer.value, skipped: answer.how === 'later' });
    return;
  }
  const askId = askIdOf(card);
  if (!askId) return;
  if (answer.how === 'later') {
    await aaiApi.skipOrbitAsk(askId);
    return;
  }
  await aaiApi.answerOrbitAsk(askId, answer.value);
};

export const performSkip = async (card: DeckCardModel): Promise<void> => {
  const personKey = personKeyOf(card);
  if (personKey) {
    await aaiApi.answerMyPart({ key: personKey, skipped: true });
    return;
  }
  const askId = askIdOf(card);
  if (!askId) return;
  await aaiApi.skipOrbitAsk(askId);
};

export const readDocument = async (file: File, clientId?: string | null): Promise<{ written: number }> => {
  const res = await ingestFile('doc', file, file.name, clientId || undefined);
  const raw = res as unknown as { deltas?: unknown[]; written?: number };
  let written = 0;
  if (typeof raw.written === 'number') written = raw.written;
  else if (Array.isArray(raw.deltas)) written = raw.deltas.length;
  return { written };
};

export const transcribeFor = (clientId?: string | null) => (blob: Blob) => transcribeAudio(blob, clientId || undefined);

export const railFromOverview = (site: string, overview: OrbitOverview | null, learned: DeckLearned[], auto: number, counts?: OrbitDeckResponse['counts']): DeckRailState => {
  const areas = (overview ? overview.areas : []).filter((a) => a.key !== 'opportunities' && a.key !== 'problem').map((a) => ({ key: a.key, label: a.label, pct: a.pct }));
  const doors = (overview ? overview.dossiers : []).filter((d) => d.kind === 'department' || d.kind === 'proposed_department').map((d) => ({ key: d.key, name: d.name.replace(/ dossier$/, ''), found: d.kind === 'department' }));
  const rail: DeckRailState = {
    site,
    facts: overview ? overview.counts.known + overview.counts.confirm : 0,
    auto,
    areas,
    learned,
    doors,
    departments: overview ? overview.counts.departments : doors.length,
  };
  if (counts) {
    rail.skipped = counts.skipped;
    rail.left = counts.left;
    rail.forYou = counts.handed;
  }
  return rail;
};

export const learnedLineFor = (card: DeckCardModel, answer: DeckAnswer): DeckLearned => {
  if (card.input === 'confirm') return { text: `Confirmed. ${(answer.confirm || []).filter((d) => d.action !== 'skip').length} facts now read as known, with you as the source.`, gold: true };
  if (card.input === 'confirm_org') return { text: `Departments and people confirmed. Orbit routes their questions to the right person from now.`, gold: true };
  const short = card.title.replace(/\?$/, '');
  return { text: `${short}: recorded with you as the source.` };
};
